import { Button, ButtonV2, Dropdown, Menu, TooltipV2 } from '@cimplico/components';
import React, { ReactNode } from 'react';
import { getChildrenByType } from '../../utilities/child-components';
import CimplicoStaffGuard from '../cimplico-staff-guard/CimplicoStaffGuard';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidElement = React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>;

type SecondaryButtonProps = {
    hidden?: boolean;
    collapsible?: boolean;
    type?: 'secondary' | 'primary' | 'danger';
    size?: 'xs' | 'sm';
    onClick?: () => void;
    label: string;
    disabled?: boolean;
    disabledHint?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    isSubmit?: boolean;
    formId?: string;
    loading?: boolean;
    staffOnly?: boolean;
};

type SecondaryPageActionsProps = {
    hidden?: boolean;
    children: ReactNode | ReactNode[];
    maxActionsCount?: number;
};

function SecondaryPageActions({ children, hidden = false, maxActionsCount = 3 }: SecondaryPageActionsProps): JSX.Element | null {
    if (hidden) {
        return null;
    }

    const _children = Array.isArray(children) ? children : [children];
    const buttons = _children.filter((c) => React.isValidElement(c) && !(c.props as SecondaryButtonProps).hidden);

    const collapsible = buttons.filter((b) => (b as ValidElement).props.collapsible !== false);
    const notCollapsible = buttons.filter((b) => (b as ValidElement).props.collapsible === false);

    return (
        <div className="flex gap-2 items-center">
            {collapsible.length > maxActionsCount ? (
                <>
                    <Dropdown label="More actions" size="small" type="secondary">
                        {collapsible.slice(maxActionsCount).map((c) => {
                            const { label, onClick, disabled, leftIcon, staffOnly } = (c as ValidElement).props;
                            const option = (
                                <Dropdown.Option
                                    label={label}
                                    onClick={onClick}
                                    value={label}
                                    key={label}
                                    disabled={disabled}
                                    leftIcon={leftIcon}
                                />
                            );
                            return staffOnly ? <CimplicoStaffGuard key={label}>{option}</CimplicoStaffGuard> : option;
                        })}
                    </Dropdown>

                    {[...collapsible.slice(0, maxActionsCount), ...notCollapsible]}
                </>
            ) : (
                buttons
            )}
        </div>
    );
}

function SecondaryButton({
    label,
    onClick,
    disabled = false,
    disabledHint = '',
    tooltipPosition = 'right',
    type = 'secondary',
    hidden = false,
    collapsible = true,
    size = 'sm',
    isSubmit = false,
    formId,
    loading,
    staffOnly = false,
}: SecondaryButtonProps): JSX.Element | null {
    if (hidden) {
        return null;
    }

    const button = (
        <TooltipV2 message={disabledHint} disabled={!(disabled && disabledHint)} position={tooltipPosition}>
            <ButtonV2
                type={type}
                label={label}
                size={size}
                onClick={(): void => {
                    if (onClick) {
                        onClick();
                    }
                }}
                isDisabled={disabled}
                isSubmit={isSubmit}
                form={formId}
                isLoading={loading}
            />
        </TooltipV2>
    );
    return staffOnly ? <CimplicoStaffGuard key={label}>{button}</CimplicoStaffGuard> : button;
}

SecondaryPageActions.Button = SecondaryButton;

export default SecondaryPageActions;
